import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Cell
} from 'recharts';
import { Activity, ArrowLeft, CheckCircle2, CornerUpLeft, ExternalLink, FileText, RotateCcw, ShieldCheck, Wand2 } from 'lucide-react';
import apiService, { apiActor } from '../../services/apiService';
import ForecastAuditTrail from './ForecastAuditTrail';
import ForecastCommentThreadPanel from './ForecastCommentThreadPanel';
import ForecastLoadReversalPanel from './ForecastLoadReversalPanel';
import {
  STATUS_LABELS,
  STATUS_TONES,
  formatMillions,
  formatPct,
  getActiveForecastUser
} from './forecastUsers';
import { buildDriverViewDeltas } from './forecastImpact';

const RIBBON_METRICS = [
  { key: 'netIncome', label: 'Net income' },
  { key: 'nii', label: 'NII' },
  { key: 'fees', label: 'Fees' },
  { key: 'expense', label: 'Expense' },
  { key: 'deposits', label: 'Deposits (EOP)' }
];

const buildWaterfall = (fy1) => {
  if (!fy1) return [];
  const steps = [
    { label: 'Baseline', total: true, value: fy1.baseline },
    { label: 'NII', value: fy1.rateVolumeVariance },
    { label: 'Fees', value: fy1.feeVariance },
    { label: 'Expense', value: fy1.expenseVariance },
    { label: 'Credit', value: fy1.creditVariance },
    { label: 'Tax', value: fy1.taxVariance },
    { label: 'Modified', total: true, value: fy1.adjusted }
  ];
  let cumulative = 0;
  return steps.map((step) => {
    if (step.total) {
      cumulative = step.value;
      return { label: step.label, base: 0, delta: step.value, tone: 'total' };
    }
    const base = step.value >= 0 ? cumulative : cumulative + step.value;
    cumulative += step.value;
    return {
      label: step.label,
      base,
      delta: Math.abs(step.value),
      tone: step.value >= 0 ? 'pos' : 'neg',
      signed: step.value
    };
  });
};

const ForecastReviewDetail = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const activeUser = getActiveForecastUser();
  const activeActor = apiActor(activeUser);
  const [record, setRecord] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [error, setError] = useState(null);
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [actionResult, setActionResult] = useState(null);
  const [roleContext, setRoleContext] = useState(null);
  const [roleContextReady, setRoleContextReady] = useState(false);
  const [assumptionThread, setAssumptionThread] = useState(null); // B4: {key, label}
  const [driverViewAnchor, setDriverViewAnchor] = useState(null); // M4: top-down anchor

  const refresh = async () => {
    const result = await apiService.fetchForecastSubmission(submissionId);
    setRecord(result);
    return result;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const result = await refresh();
        if (cancelled) return;
        if (result.status === 'submitted') {
          apiService.transitionForecastSubmission(submissionId, {
            action: 'open_review',
            actor: activeActor
          }).catch(() => {});
        }
        const comparison = await apiService.fetchForecastComparison({
          cycleCode: result.cycle_code,
          level: 'entity',
          entity: result.planning_entity,
          grain: 'monthly'
        });
        if (!cancelled) setMonthly(comparison.groups?.[0] || null);
      } catch (err) {
        if (!cancelled) setError(err?.response?.data?.error || err.message);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId]);

  useEffect(() => {
    let cancelled = false;
    setRoleContextReady(false);
    apiService.fetchForecastRoleContext(activeUser.name, 'FY2027_BUDGET')
      .then((ctx) => {
        if (!cancelled) {
          setRoleContext(ctx);
          setRoleContextReady(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setRoleContext(null);
          setRoleContextReady(true);
        }
      });
    return () => { cancelled = true; };
  }, [activeUser.name]);

  // M4: fetch the active driver-view anchor so the reviewer sees the plan's
  // deviations from the top-down anchor next to the assumption diff.
  useEffect(() => {
    if (!record?.planning_entity) {
      setDriverViewAnchor(null);
      return undefined;
    }
    let cancelled = false;
    apiService.fetchDriverViewAnchor({
      entity: record.planning_entity,
      cycleCode: record.cycle_code || 'FY2027_BUDGET',
      scenarioKey: record.model_snapshot?.macroScenarioKey || 'Base'
    })
      .then((result) => {
        if (!cancelled) setDriverViewAnchor(result?.anchor || null);
      })
      .catch(() => {
        if (!cancelled) setDriverViewAnchor(null);
      });
    return () => { cancelled = true; };
  }, [record?.planning_entity, record?.cycle_code, record?.model_snapshot?.macroScenarioKey]);

  const lineageRows = useMemo(() => {
    const lineage = record?.inputs?.lineage || {};
    return Object.values(lineage).sort((a, b) => String(b.timestamp).localeCompare(String(a.timestamp)));
  }, [record]);

  // M4: plan-vs-anchor deltas + the reviewer-facing narratives keyed to breaches.
  const driverViewDeltas = useMemo(() => {
    const anchorSnapshot = driverViewAnchor?.anchorSnapshot;
    const planByYear = record?.model_snapshot?.driverView?.byYear;
    if (!anchorSnapshot || !planByYear) return [];
    return buildDriverViewDeltas({ anchorSnapshot, planByYear });
  }, [driverViewAnchor, record]);

  const driverViewNarratives = useMemo(() => {
    const list = Array.isArray(record?.narratives) ? record.narratives : [];
    return Object.fromEntries(
      list
        .filter((n) => String(n.trigger || '').startsWith('driver-view-'))
        .map((n) => [n.trigger, n.text])
    );
  }, [record]);

  const fy1 = record?.model_snapshot?.annualResults?.[0] || null;
  const waterfall = useMemo(() => buildWaterfall(fy1), [fy1]);
  const constraintSummary = record?.model_snapshot?.constraintSummary || null;
  const redCount = constraintSummary?.red || 0;

  const ribbons = useMemo(() => {
    if (!monthly) return [];
    const priorByPeriod = Object.fromEntries((monthly.series.prior || []).map((row) => [row.period, row]));
    const periods = [...new Set([
      ...(monthly.series.actuals || []).map((row) => row.period),
      ...(monthly.series.prior || []).map((row) => row.period)
    ])].sort();
    const window = periods.filter((period) => period >= '2025-01' && period <= '2027-12');
    const actualByPeriod = Object.fromEntries((monthly.series.actuals || []).map((row) => [row.period, row]));
    return RIBBON_METRICS.map((metric) => ({
      ...metric,
      data: window.map((period) => ({
        period,
        actual: actualByPeriod[period] ? actualByPeriod[period][metric.key] / 1e6 : null,
        prior: priorByPeriod[period] ? priorByPeriod[period][metric.key] / 1e6 : null
      }))
    }));
  }, [monthly]);

  const act = async (action, extraPayload = {}) => {
    setBusy(true);
    setActionResult(null);
    try {
      await apiService.transitionForecastSubmission(submissionId, {
        action,
        actor: activeActor,
        comment,
        payload: { constraintRed: redCount, ...extraPayload }
      });
      setComment('');
      await refresh();
      setActionResult({ tone: 'ok', message: `${action} complete` });
    } catch (err) {
      setActionResult({ tone: 'error', message: err?.response?.data?.error || err.message });
    } finally {
      setBusy(false);
    }
  };

  // Item G — FP&A Quick Fix ledger: the FP&A adjustment layer lives in 'adjust'
  // events tagged scenario 'FP&A Adjusted' (the market submission is untouched).
  const clientCanQuickFix = activeUser.role === 'manager';
  const canQuickFix = !roleContextReady
    ? clientCanQuickFix
    : (roleContext?.permissions || []).includes('adjust:quick_fix');
  // B3: the "Reverse load" affordance is only shown when the server grants
  // load:unload (central FP&A). Never inferred from client role labels.
  const canUnload = roleContextReady
    && (roleContext?.permissions || []).includes('load:unload');
  const fpaEvents = useMemo(
    () => (record?.events || []).filter(
      (event) => event.event === 'adjust' && event.payload?.scenario === 'FP&A Adjusted'
    ),
    [record]
  );
  const fpaCurrent = fpaEvents[0]?.payload || null;
  const marketNiByYear = useMemo(
    () => Object.fromEntries((record?.model_snapshot?.annualResults || []).map((row) => [row.year, row.adjusted])),
    [record]
  );
  const fpaNiDelta = useMemo(() => {
    if (!fpaCurrent?.annualAfter) return null;
    const entries = Object.entries(fpaCurrent.annualAfter)
      .map(([year, ni]) => ({ year: Number(year), delta: (Number(ni) || 0) - (Number(marketNiByYear[Number(year)]) || 0) }))
      .filter((entry) => Math.abs(entry.delta) >= 0.05)
      .sort((a, b) => a.year - b.year);
    return entries.length ? entries : null;
  }, [fpaCurrent, marketNiByYear]);

  const launchQuickFix = () => navigate(`/forecasting/input/${record.id}?adjust=fpa`);

  const undoLastFpa = async () => {
    if (!fpaEvents.length) return;
    setBusy(true);
    setActionResult(null);
    try {
      const prior = fpaEvents[1]?.payload || null;
      await apiService.recordForecastAdjustment(submissionId, {
        actor: activeActor,
        comment: 'Reverted the last FP&A Quick Fix adjustment',
        payload: {
          source: 'FP&A Quick Fix · undo', scenario: 'FP&A Adjusted', kind: 'undo',
          actingAs: activeActor,
          summary: prior ? 'Reverted to the previous FP&A adjustment' : 'Reverted to the market submission',
          leversAfter: prior?.leversAfter || record.inputs?.yearAdjustments || {},
          quarterlyAfter: prior?.quarterlyAfter || record.inputs?.quarterlyGrowth || {},
          annualAfter: prior?.annualAfter || null,
          planningEntity: record?.planning_entity,
          cycleCode: record?.cycle_code || 'FY2027_BUDGET'
        }
      });
      await refresh();
      setActionResult({ tone: 'ok', message: 'FP&A adjustment reverted' });
    } catch (err) {
      setActionResult({ tone: 'error', message: err?.response?.data?.error || err.message });
    } finally {
      setBusy(false);
    }
  };

  if (error) {
    return <div className="forecast-module"><div className="forecast-panel forecast-home-error"><p>{error}</p></div></div>;
  }
  if (!record) {
    return <div className="forecast-module"><div className="forecast-panel"><p className="forecast-home-loading">Loading review…</p></div></div>;
  }

  const narratives = Array.isArray(record.narratives) ? record.narratives : [];

  return (
    <div className="forecast-module forecast-review-detail">
      <section className="forecast-command">
        <div>
          <div className="forecast-kicker">
            <Activity size={14} />
            FP&A review / package drill-in
            <Link to="/forecasting/review" className="forecast-kicker-back">
              <ArrowLeft size={12} /> Review console
            </Link>
          </div>
          <h1>{record.division} · {record.market}</h1>
          <p>
            <span className={`forecast-status-chip ${STATUS_TONES[record.status]}`}>{STATUS_LABELS[record.status]}</span>
            {record.submitted_by && (
              <span className="forecast-review-meta">
                Submitted by {record.submitted_by} · {String(record.submitted_at || '').slice(0, 16).replace('T', ' ')}
              </span>
            )}
          </p>
        </div>
        <div className="forecast-command-grid">
          <Link className="forecast-home-link" to={`/forecasting/statements?level=entity&entity=${record.planning_entity}`}>
            <FileText size={14} /> Statement view
          </Link>
          <Link className="forecast-home-link" to={`/forecasting/input/${record.id}`}>
            <ExternalLink size={14} /> Open full workspace
          </Link>
        </div>
      </section>

      {narratives.length > 0 && (
        <section className="forecast-panel forecast-narrative-panel">
          <div className="forecast-panel-head">
            <span className="forecast-section-code">RV_A ● The market's argument</span>
          </div>
          {narratives.map((narrative) => (
            <blockquote key={narrative.trigger}>
              <small>{narrative.label}</small>
              <p>{narrative.text || '—'}</p>
            </blockquote>
          ))}
        </section>
      )}

      <section className="forecast-panel">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">RV_B ● What they changed</span>
          <h2>Assumption diff — only edited fields, with provenance</h2>
        </div>
        {lineageRows.length === 0 ? (
          <p className="forecast-submit-note">No assumption edits recorded — this package is the central baseline.</p>
        ) : (
          <div className="forecast-home-table-wrap">
            <table className="forecast-home-table forecast-diff-table">
              <thead>
                <tr>
                  <th>Assumption</th>
                  <th>Central → Market</th>
                  <th>Who</th>
                  <th>When</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {lineageRows.map((row) => (
                  <tr key={row.key}>
                    <td>{row.field}</td>
                    <td>
                      <span className="forecast-mono">{String(row.priorValue)}</span>
                      <span className="forecast-diff-arrow"> → </span>
                      <strong className="forecast-mono">{String(row.newValue)}</strong>
                    </td>
                    <td>{row.user}</td>
                    <td>{row.timestamp}</td>
                    <td>
                      <button
                        type="button"
                        className="forecast-home-action ghost"
                        onClick={() => setAssumptionThread(
                          assumptionThread?.rawKey === row.key
                            ? null
                            : { rawKey: row.key, key: `assumption:${row.key}`, label: row.field }
                        )}
                      >
                        Discuss
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {assumptionThread && (
          <ForecastCommentThreadPanel
            submissionId={submissionId}
            scopeType="assumption"
            assumptionKey={assumptionThread.key}
            fieldLabel={assumptionThread.label}
            compact
          />
        )}
      </section>

      {driverViewAnchor && driverViewDeltas.length > 0 && (
        <section className="forecast-panel">
          <div className="forecast-panel-head">
            <span className="forecast-section-code">RV_B2 ● Plan vs driver view</span>
            <h2>
              Deviations from the top-down anchor (Base v{driverViewAnchor.scenarioVersion ?? '—'})
            </h2>
          </div>
          <div className="forecast-home-table-wrap">
            <table className="forecast-home-table forecast-diff-table">
              <thead>
                <tr>
                  <th>Line</th>
                  <th>Year</th>
                  <th>Driver view → Plan</th>
                  <th>Deviation</th>
                  <th>Justification</th>
                </tr>
              </thead>
              <tbody>
                {driverViewDeltas
                  .filter((delta) => delta.exceeded)
                  .map((delta) => (
                    <tr key={delta.narrativeTrigger}>
                      <td>{delta.metricLabel}</td>
                      <td>FY{delta.year}</td>
                      <td>
                        <span className="forecast-mono">{formatMillions(delta.anchor)}</span>
                        <span className="forecast-diff-arrow"> → </span>
                        <strong className="forecast-mono">{formatMillions(delta.plan)}</strong>
                      </td>
                      <td className={delta.deviationPct >= 0 ? 'variance-pos' : 'variance-neg'}>
                        {delta.deviationPct == null
                          ? '—'
                          : `${delta.deviationPct >= 0 ? '+' : ''}${delta.deviationPct.toFixed(1)}%`}
                      </td>
                      <td>{driverViewNarratives[delta.narrativeTrigger] || <em>— no narrative —</em>}</td>
                    </tr>
                  ))}
                {driverViewDeltas.every((delta) => !delta.exceeded) && (
                  <tr>
                    <td colSpan={5}>No lines exceed the justification thresholds.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <section className="forecast-panel">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">RV_C ● What it does</span>
          <h2>
            {fy1
              ? `${String(fy1.year).startsWith('FY') ? fy1.year : `FY${fy1.year}`} bridge: baseline ${formatMillions(fy1.baseline)} → modified ${formatMillions(fy1.adjusted)} (${formatPct(fy1.variancePct)})`
              : 'No model snapshot saved yet'}
          </h2>
        </div>
        {fy1 && (() => {
          const tops = waterfall.map((row) => (row.tone === 'total' ? row.delta : row.base + row.delta));
          const bottoms = waterfall.map((row) => (row.tone === 'total' ? row.delta : row.base));
          const hi = Math.max(...tops, ...bottoms);
          const lo = Math.min(...tops, ...bottoms);
          const span = Math.max(hi - lo, Math.abs(hi) * 0.05, 1);
          const domainMin = lo - span * 0.12;
          const domainMax = hi + span * 0.08;
          const rows = waterfall.map((row) => ({
            ...row,
            range: row.tone === 'total' ? [domainMin, row.delta] : [row.base, row.base + row.delta]
          }));
          return (
            <div style={{ width: '100%', height: 260 }}>
              <ResponsiveContainer>
                <ComposedChart data={rows}>
                  <CartesianGrid strokeDasharray="2 4" strokeOpacity={0.25} />
                  <XAxis dataKey="label" />
                  <YAxis domain={[domainMin, domainMax]} tickFormatter={(value) => `$${Math.round(value)}MM`} width={70} allowDataOverflow />
                  <Tooltip
                    formatter={(value, name, props) => {
                      const row = props?.payload;
                      return [formatMillions(row?.tone === 'total' ? row.delta : row?.signed), row?.label];
                    }}
                  />
                  <Bar dataKey="range" radius={[3, 3, 0, 0]} isAnimationActive={false}>
                    {rows.map((row) => (
                      <Cell
                        key={row.label}
                        fill={row.tone === 'total' ? 'var(--ace-chart-2, #6b7c93)' : row.tone === 'pos' ? '#2f9e6e' : '#d9534f'}
                      />
                    ))}
                  </Bar>
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          );
        })()}
        {constraintSummary && (
          <div className="forecast-submit-chips" style={{ marginTop: 10 }}>
            <span className={`forecast-health-chip ${constraintSummary.red ? 'red' : constraintSummary.yellow ? 'yellow' : 'green'}`}>
              Constraints: {constraintSummary.green} green / {constraintSummary.yellow} watch / {constraintSummary.red} breach
            </span>
            <span className="forecast-health-chip neutral">{record.model_snapshot?.overrides ?? 0} active inputs</span>
            <span className="forecast-health-chip neutral">Scenario {record.model_snapshot?.scenarioVersion || '—'}</span>
          </div>
        )}
      </section>

      <section className="forecast-panel">
        <div className="forecast-panel-head">
          <span className="forecast-section-code">RV_D ● Does the story hold</span>
          <h2>Actuals (solid) vs prior cycle plan (dashed) — management view, $MM</h2>
        </div>
        <div className="forecast-ribbon-grid">
          {ribbons.map((ribbon) => (
            <div className="forecast-ribbon" key={ribbon.key}>
              <h4>{ribbon.label}</h4>
              <div style={{ width: '100%', height: 120 }}>
                <ResponsiveContainer>
                  <ComposedChart data={ribbon.data}>
                    <XAxis dataKey="period" hide />
                    <YAxis hide domain={['auto', 'auto']} />
                    <Tooltip formatter={(value) => [`$${Number(value).toFixed(2)}MM`]} labelFormatter={(label) => label} />
                    <Line dataKey="prior" stroke="var(--ace-chart-2, #6b7c93)" strokeDasharray="4 3" dot={false} strokeWidth={1.5} name="Prior plan" />
                    <Line dataKey="actual" stroke="#FFAC03" dot={false} strokeWidth={2} name="Actuals" />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            </div>
          ))}
        </div>
      </section>

      {canQuickFix && (
        <section className="forecast-panel forecast-quickfix-panel">
          <div className="forecast-panel-head">
            <span className="forecast-section-code">RV_QF ● Quick Fix — FP&amp;A adjustment</span>
            <h2>
              <ShieldCheck size={15} style={{ marginRight: 6, verticalAlign: '-2px' }} />
              Last-mile adjustment in the FP&amp;A layer — the market submission stays intact
            </h2>
          </div>
          <p className="forecast-submit-note">
            Adjust this package without overwriting what {record.market} submitted. Changes write to the
            <strong> FP&amp;A Adjusted</strong> layer, are attributed to you, reconcile through the model, and revert exactly.
            Use the AI prompt box (plain English) or edit the OP_03 levers directly — both reuse the same
            second-order driver propagation.
          </p>
          <div className="forecast-submit-chips" style={{ marginBottom: 10 }}>
            <button type="button" className="forecast-primary-button compact" onClick={launchQuickFix}>
              <Wand2 size={13} style={{ marginRight: 5, verticalAlign: '-2px' }} />
              Open Quick Fix workspace
            </button>
            {fpaEvents.length > 0 && (
              <button type="button" className="forecast-secondary-button compact" onClick={undoLastFpa} disabled={busy}>
                <RotateCcw size={13} style={{ marginRight: 5, verticalAlign: '-2px' }} />
                Undo last adjustment
              </button>
            )}
            <span className="forecast-health-chip neutral">
              {fpaEvents.length} FP&amp;A adjustment{fpaEvents.length === 1 ? '' : 's'} on file
            </span>
            {fpaNiDelta && fpaNiDelta.map((entry) => (
              <span key={entry.year} className={`forecast-health-chip ${entry.delta >= 0 ? 'green' : 'red'}`}>
                {String(entry.year).startsWith('FY') ? entry.year : `FY${entry.year}`} net income {entry.delta >= 0 ? '+' : ''}{formatMillions(entry.delta)} vs market
              </span>
            ))}
          </div>
          {fpaCurrent && (
            <div className="forecast-home-table-wrap">
              <table className="forecast-home-table forecast-diff-table">
                <thead>
                  <tr><th>FP&amp;A adjustment</th><th>What changed</th><th>Who</th><th>When</th></tr>
                </thead>
                <tbody>
                  {fpaEvents.map((event) => {
                    const payload = event.payload || {};
                    const changeLabels = (payload.leverChanges || []).map((change) => change.label).join(', ');
                    return (
                      <tr key={`${event.created_at}-${payload.kind}`}>
                        <td>
                          <span className={`forecast-health-chip ${payload.kind === 'undo' ? 'neutral' : 'gold'}`}>
                            {payload.kind === 'ai' ? 'AI prompt' : payload.kind === 'explicit' ? 'Manual override' : 'Undo'}
                          </span>
                        </td>
                        <td>{payload.summary || changeLabels || '—'}</td>
                        <td>{payload.actingAs || event.actor}</td>
                        <td>{String(event.created_at || '').slice(0, 16).replace('T', ' ')}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <ForecastAuditTrail submissionId={submissionId} initialEvents={record.events} />

      <ForecastCommentThreadPanel submissionId={submissionId} scopeType="package" />

      {record.status === 'loaded' && canUnload && (
        <ForecastLoadReversalPanel
          submissionId={submissionId}
          actor={activeActor}
          canUnload={canUnload}
          onReversed={() => { refresh(); }}
        />
      )}

      <section className="forecast-decision-bar">
        <textarea
          rows={2}
          placeholder={
            redCount > 0
              ? 'Override note required to approve (red constraints) — or return with comments…'
              : 'Comment (required to return or reopen)…'
          }
          value={comment}
          onChange={(event) => setComment(event.target.value)}
        />
        <div className="forecast-decision-actions">
          {record.status === 'submitted' && (
            <>
              <button
                type="button"
                className="forecast-primary-button"
                disabled={busy || (redCount > 0 && !comment.trim())}
                onClick={() => act('approve')}
              >
                <CheckCircle2 size={15} /> {redCount > 0 ? 'Approve with override note' : 'Approve'}
              </button>
              <button
                type="button"
                className="forecast-secondary-button"
                disabled={busy || !comment.trim()}
                onClick={() => act('return')}
              >
                <CornerUpLeft size={15} /> Return with comments
              </button>
            </>
          )}
          {(['submitted', 'approved'].includes(record.status)
            || (record.status === 'loaded' && !canUnload)) && (
            <button
              type="button"
              className="forecast-secondary-button"
              disabled={busy || !comment.trim()}
              onClick={() => act('reopen')}
            >
              <RotateCcw size={15} /> Reopen for edits
            </button>
          )}
          {record.status === 'approved' && (
            <button
              type="button"
              className="forecast-home-action"
              onClick={() => navigate('/forecasting/review')}
            >
              Go to load center
            </button>
          )}
          {actionResult && (
            <span className={`forecast-health-chip ${actionResult.tone === 'ok' ? 'green' : 'red'}`}>
              {actionResult.message}
            </span>
          )}
        </div>
      </section>
    </div>
  );
};

export default ForecastReviewDetail;
